import React, { ComponentType, createContext, useContext } from "react"
import { On401 } from "../services/request"

// What a page wants done when the API refuses the session (see On401). The
// page sets it once; every useAPI() below it - including shared components
// like the order details modal - inherits it, so no call can replace a
// checkout's session behind the order's back. An explicit option on useAPI()
// or on a single call still wins.
type SessionPolicy = { on401?: On401 }

const SessionPolicyContext = createContext<SessionPolicy>({})

export const SessionPolicyProvider = ({
  on401,
  children,
}: SessionPolicy & { children: React.ReactNode }) => (
  <SessionPolicyContext.Provider value={{ on401 }}>
    {children}
  </SessionPolicyContext.Provider>
)

export const useSessionPolicy = () => useContext(SessionPolicyContext)

// For Gatsby pages, which have to export the component itself. "throw" only
// makes sense with something mounted to catch what is thrown, so the handler
// is part of the wrap - a page cannot get the policy without it. The handler
// receives the page props and renders nothing.
export const withSessionPolicy =
  <P extends object>(on401: On401, Handler: ComponentType<P>) =>
  (Page: ComponentType<P>) => {
    const WithSessionPolicy = (props: P) => (
      <SessionPolicyProvider on401={on401}>
        <Handler {...props} />
        <Page {...props} />
      </SessionPolicyProvider>
    )
    WithSessionPolicy.displayName = `withSessionPolicy(${
      Page.displayName || Page.name || "Page"
    })`
    return WithSessionPolicy
  }
